import * as Components from '../components'
import * as Form from '../form'
import * as Hooks from '../hooks'
import * as Navigation from '../navigation'
import * as Stores from '../stores'
import { H6, View, YStack } from 'tamagui'
import { z } from 'zod/v4'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { router } from 'expo-router'

const SensitiveInfoFormSchema = Form.CardApplicationFormSchema.pick({
  ssn: true,
  totalAnnualIncome: true,
  monthlyHousingPayment: true,
})
type SensitiveInfoFormValues = z.infer<typeof SensitiveInfoFormSchema>
const DefaultValues = {
  ssn: '',
  totalAnnualIncome: '',
  monthlyHousingPayment: '',
} satisfies SensitiveInfoFormValues

const containsValues = (values: SensitiveInfoFormValues) =>
  values.ssn && values.totalAnnualIncome && values.monthlyHousingPayment

export function SensitiveInfoScreen() {
  const updateField = Stores.useCardApplicationFormStore((s) => s.updateField)
  const cardApplicationForm = Stores.useCardApplicationFormStore((s) => s.cardApplicationForm)
  const defaultValues = containsValues(cardApplicationForm) ? cardApplicationForm : DefaultValues

  const form = useForm<SensitiveInfoFormValues>({
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(SensitiveInfoFormSchema),
  })
  const { registerNav } = Hooks.useFormNav(form, [
    'ssn',
    'totalAnnualIncome',
    'monthlyHousingPayment',
  ])

  const onSubmit = form.handleSubmit((data) => {
    updateField('ssn', data.ssn)
    updateField('totalAnnualIncome', data.totalAnnualIncome)
    updateField('monthlyHousingPayment', data.monthlyHousingPayment)
    router.push(Navigation.Routes.ApplicationConfirmation.route)
  })

  return (
    <View flex={1} bg="$white1" pb="$8">
      <Components.ApplicationScreen>
        <Components.ApplicationScreen.Header previousRoute={Navigation.Routes.FindYourHome} />
        <Components.ApplicationScreen.Content title="Last questions">
          <Components.FormHelpText>Then we're ready to review</Components.FormHelpText>
          <Components.FormInputSsn
            control={form.control}
            name="ssn"
            {...Form.FormInputProps.ssn}
            {...registerNav('ssn')}
          />
          <Components.FormInputCurrency
            control={form.control}
            name="totalAnnualIncome"
            {...Form.FormInputProps.totalAnnualIncome}
            {...registerNav('totalAnnualIncome')}
          />
          <YStack>
            <Components.FormHelpText>Make sure to factor in:</Components.FormHelpText>
            <Components.FormHelpBullet>
              Your yearly salary, wages, commissions, and tips before taxes
            </Components.FormHelpBullet>
            <Components.FormHelpBullet>
              Any income from a spouse, partner, family member, or the government that you rely on.
            </Components.FormHelpBullet>
            <Components.FormHelpBullet>
              Government benefits, public assistance, retirement benefits, investments and rent paid
              to you.
            </Components.FormHelpBullet>
            <Components.FormHelpBullet>
              Alimony, child support, or separate Maintenance need is not required unless you want
              it to be considered as a basis for repaying this obligation.
            </Components.FormHelpBullet>
          </YStack>
          <Components.FormInputCurrency
            control={form.control}
            name="monthlyHousingPayment"
            {...Form.FormInputProps.monthlyHousingPayment}
            {...registerNav('monthlyHousingPayment')}
          />
          <Components.FormHelpText>
            This should be your total mortgage payment or whatever you pay in rent for a single
            month. This only includes payments toward housing, not expenses like gas or electric. If
            you don't have a housing payment, you can type in "0."
          </Components.FormHelpText>
          <YStack mt="$4">
            <H6>IMPORTANT INFORMATION ABOUT PROCEDURES FOR OPENING A NEW ACCOUNT:</H6>
            <Components.FormHelpText>
              To help the government fight the funding of terrorism and money laundering activities,
              Federal law requires all financial institutions to obtain, verify and record
              information that identifies each person who opens an account. What this means for you:
              When you open an account, we will ask for your name, address, date of birth and other
              information that will allow us to identify you. We may also ask to see your driver's
              license or other identifying documents.
            </Components.FormHelpText>
          </YStack>
        </Components.ApplicationScreen.Content>
        <Components.ApplicationScreen.SubmitButton
          onPress={() => void onSubmit()}
          disabled={!form.formState.isValid}
          loading={form.formState.isSubmitting}
          title="Continue"
        />
      </Components.ApplicationScreen>
    </View>
  )
}
